import { Box, Button, Chip, Typography, Alert } from '@mui/material';
import CircularProgress from "@mui/material/CircularProgress";
import DownloadIcon from '@mui/icons-material/Download';
import { AuthContext } from "context";
import { createMRTColumnHelper } from 'material-react-table';
import { useContext, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import MDAlert from "../../components/MDAlert";
import MDBox from "../../components/MDBox";
import DashboardLayout from "../../examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "../../examples/Navbars/DashboardNavbar";
import apiService from "../../services/api-service";
import GenericTable from "../genericTable";

function ManagementSecurityBottoli() {
  const authContext = useContext(AuthContext);
  const navigate = useNavigate();
  const [companyDocuments, setCompanyDocuments] = useState([]);
  const [loadingDocuments, setLoadingDocuments] = useState(false);
  const [bottoliSupplier, setBottoliSupplier] = useState(null);
  const [error, setError] = useState(null);
  const [downloadingZip, setDownloadingZip] = useState(false);
  const companyColumnHelper = createMRTColumnHelper();

  const handleSetNotification = (msg, severityType) => {
    if (severityType === 'success') {
      toast.success(msg, { id: 'managment-security-bottoli-toast' });
    } else {
      toast.error(msg, { id: 'managment-security-bottoli-toast' });
    }
  };

  function fetchBottoliSupplier() {
    setLoadingDocuments(true);
    setError(null);

    apiService.get("/suppliers-management", { get_bottoli: true }, {})
      .then((response) => {
        if (response && response.id) {
          setBottoliSupplier(response);
          fetchBottoliDocuments(response.id);
        } else {
          setError("Supplier Bottoli non trovato nel sistema");
          setLoadingDocuments(false);
        }
      })
      .catch(error => {
        handleSetNotification(
          "Errore durante il caricamento del supplier Bottoli",
          "error"
        );
        setError("Errore durante il caricamento");
        setLoadingDocuments(false);
      });
  }

  function fetchBottoliDocuments(supplierId) {
    apiService.get("/get-documents", { supplier_id: supplierId }, {})
      .then((response) => {
        if (response) {
          setCompanyDocuments(response);
        }
        setLoadingDocuments(false);
      })
      .catch(error => {
        handleSetNotification(
          "Errore durante il caricamento dei documenti",
          "error"
        );
        setLoadingDocuments(false);
      });
  }

  useEffect(() => {
    fetchBottoliSupplier();
  }, []);

  const companyColumns = [
    companyColumnHelper.accessor('label', {
      header: 'Nome',
      size: 150,
    }),
    companyColumnHelper.accessor('status', {
      header: 'Stato',
      size: 120,
      Cell: ({ cell }) => (
        <Chip
          label={getStatusName(cell.getValue())}
          color={getStatusColor(cell.getValue(), cell.row.original.required)}
          size="medium"
          sx={{ fontWeight: 'medium' }}
        />
      ),
    }),
    companyColumnHelper.accessor('required', {
      header: 'Richiesto',
      size: 100,
      Cell: ({ cell }) => (
        <Typography variant="body2">
          {cell.getValue() ? 'Necessario' : 'Opzionale'}
        </Typography>
      ),
    }),
    companyColumnHelper.accessor('additionalInformation', {
      header: 'Informazioni Aggiuntive',
      size: 250,
      Cell: ({ cell }) => (
        <Box>
          {cell.getValue()?.map((info, index) => (
            <Typography key={index} variant="body2">
              {info.label}: {info.value || 'N/A'}
            </Typography>
          ))}
        </Box>
      ),
    }),
    companyColumnHelper.accessor('url', {
      header: 'Azioni',
      size: 100,
      Cell: ({ cell }) => (
        cell.row.original.status !== 'toUpload' &&
        <Button
          variant="outlined"
          size="small"
          onClick={() => window.open(cell.getValue(), '_blank')}
          sx={{
            marginTop: "10px",
            marginBottom: "30px",
            backgroundColor: '#514f4f',
            color: 'white!important',
            mb: 2,
            width: '80px',
            height: '25px',
            fontSize: '10px',
            display: 'flex',
            alignItems: 'center',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1), 0 1px 3px rgba(0, 0, 0, 0.06)',
            justifyContent: 'center',
            borderColor: 'black',
            gap: '8px',
            '&:hover': {
              backgroundColor: '#514f4f',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1), 0 1px 3px rgba(0, 0, 0, 0.06)',
              color: 'white!important',
            },
            '&:focus': {
              backgroundColor: '#514f4f',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1), 0 1px 3px rgba(0, 0, 0, 0.06)',
              color: 'white!important',
            },
            '&:active': {
              backgroundColor: '#514f4f',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1), 0 1px 3px rgba(0, 0, 0, 0.06)',
              color: 'white!important',
            }
          }}
        >
          Visualizza
        </Button>
      ),
    }),
  ];

  const handleNavigateToUpload = () => {
    if (bottoliSupplier) {
      navigate(`/security-document-upload/supplier/?supplier_id=${bottoliSupplier.id}`);
    }
  };

  const handleDownloadZip = async () => {
    setDownloadingZip(true);
    try {
      const params = new URLSearchParams({
        download_type: 'bottoli_company'
      });

      const token = localStorage.getItem("token");
      const baseUrl = window.location.hostname.split(".")[0] !== "localhost"
        ? `https://${window.location.hostname.split(".")[0]}.helpmate.it/api`
        : process.env.REACT_APP_API_URL_BACKEND;

      const response = await fetch(
        `${baseUrl}/download-security-documents-zip/?${params.toString()}`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
          credentials: 'include',
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Download failed');
      }

      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = 'documenti_aziendali_bottoli.zip';
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="(.+)"/);
        if (match) filename = match[1];
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success('Download completato', { id: 'download-zip-toast' });
    } catch (error) {
      toast.error(error.message || 'Errore durante il download', { id: 'download-zip-toast' });
      console.error('Download error:', error);
    }
    setDownloadingZip(false);
  };

  return (
    <DashboardLayout>
      {authContext.hasAuth("VIEW_DOCUMENTS_SUPPLIER") ? (
        <>
          <DashboardNavbar />
          <Box sx={{ p: 3 }}>
            <Box sx={{ mb: 4 }}>
              <center>
                <Typography variant="h5" gutterBottom>
                  DOCUMENTI AZIENDA BOTTOLI
                </Typography>

                {error && (
                  <Alert severity="error" sx={{ maxWidth: 500, margin: '20px auto' }}>
                    {error}
                  </Alert>
                )}

                {authContext?.hasAuth("UPSERT_DOCUMENTS_SUPPLIER") && !loadingDocuments && bottoliSupplier && (
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleNavigateToUpload}
                    sx={{
                      marginTop: "10px",
                      marginBottom: "30px",
                      backgroundColor: '#514f4f',
                      color: 'white!important',
                      mb: 2,
                      width: '220px',
                      height: '50px',
                      fontSize: '16px',
                      display: 'flex',
                      alignItems: 'center',
                      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1), 0 1px 3px rgba(0, 0, 0, 0.06)',
                      justifyContent: 'center',
                      gap: '8px',
                      '&:hover': {
                        backgroundColor: '#514f4f',
                        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1), 0 1px 3px rgba(0, 0, 0, 0.06)',
                        color: 'white!important',
                      },
                      '&:focus': {
                        backgroundColor: '#514f4f',
                        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1), 0 1px 3px rgba(0, 0, 0, 0.06)',
                        color: 'white!important',
                      },
                      '&:active': {
                        backgroundColor: '#514f4f',
                        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1), 0 1px 3px rgba(0, 0, 0, 0.06)',
                        color: 'white!important',
                      }
                    }}
                  >
                    Gestisci Documenti
                  </Button>
                )}

                {!loadingDocuments && companyDocuments.length > 0 && (
                  <Button
                    variant="contained"
                    onClick={handleDownloadZip}
                    disabled={downloadingZip}
                    startIcon={downloadingZip ? <CircularProgress size={20} color="inherit" /> : <DownloadIcon />}
                    sx={{
                      marginTop: "10px",
                      marginBottom: "30px",
                      backgroundColor: '#514f4f',
                      color: 'white!important',
                      mb: 2,
                      width: '280px',
                      height: '50px',
                      fontSize: '14px',
                      display: 'flex',
                      alignItems: 'center',
                      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1), 0 1px 3px rgba(0, 0, 0, 0.06)',
                      justifyContent: 'center',
                      gap: '8px',
                      '&:hover': {
                        backgroundColor: '#3d3b3b',
                        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1), 0 1px 3px rgba(0, 0, 0, 0.06)',
                        color: 'white!important',
                      },
                      '&:disabled': {
                        backgroundColor: '#7a7777',
                        color: 'rgba(255,255,255,0.7)!important',
                      }
                    }}
                  >
                    {downloadingZip ? 'Download in corso...' : 'Scarica Documenti Aziendali'}
                  </Button>
                )}

                {loadingDocuments && (
                  <CircularProgress size={120} sx={{ marginTop: "250px", color: "#2b84ea" }} />
                )}
              </center>

              {!loadingDocuments && companyDocuments.length > 0 && (
                <GenericTable
                  data={companyDocuments}
                  columns={companyColumns}
                />
              )}

              {!loadingDocuments && companyDocuments.length === 0 && bottoliSupplier && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  Nessun documento trovato per Bottoli
                </Alert>
              )}
            </Box>
          </Box>
        </>
      ) : (
        <MDBox mt={8}>
          <MDAlert color="error">Non hai i permessi per visualizzare questa pagina</MDAlert>
        </MDBox>
      )}
    </DashboardLayout>
  );
}

function getStatusColor(status, required) {
  switch (status.toLowerCase()) {
    case 'approved':
      return 'success';
    case 'rejecteddone':
      return 'info';
    case 'toapprove':
      return 'warning';
    case 'toupload':
      if (required) {
        return 'error';
      }
      return 'secondary';
    case 'rejected':
      return 'error';
    default:
      return 'default';
  }
}

function getStatusName(status) {
  switch (status.toLowerCase()) {
    case 'approved':
      return 'Approvato';
    case 'rejecteddone':
      return 'Rettificato';
    case 'toapprove':
      return 'In attesa';
    case 'toupload':
      return "Da caricare"
    case 'rejected':
      return 'Da rettificare';
    default:
      return 'default';
  }
}

export default ManagementSecurityBottoli;
